'use client'

import { useState, useEffect } from 'react'
import { Button } from '@creanizer/ui'
import { Edit } from 'lucide-react'
import { SceneTextViewer } from './SceneTextViewer'
import { SceneEditModal } from './SceneEditModal'
import type { SceneResult as SceneResultType } from '@creanizer/types'

interface SceneResultProps {
  result: SceneResultType
  onSave: (result: SceneResultType) => void
  isSaved: boolean
}

export function SceneResult({ result, onSave, isSaved }: SceneResultProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedResult, setEditedResult] = useState(result)
  
  // resultプロップが変更されたら、editedResultも更新する
  useEffect(() => {
    setEditedResult(result)
  }, [result])
  
  // エラーチェック
  if (!result) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        結果が生成されていません
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      {/* タイトルと基本情報 */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold">{editedResult.title || '無題'}</h3>
          <div className="text-sm text-muted-foreground mt-1">
            {editedResult.wordCount || 0}文字 • 読了時間: {editedResult.readingTime || '不明'}
          </div>
          {editedResult.summary && (
            <p className="text-sm mt-2 text-muted-foreground">
              {editedResult.summary}
            </p>
          )}
        </div>
        <div className="flex items-start gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <Edit className="h-4 w-4 mr-2" />
            編集
          </Button>
          <div className="text-sm text-muted-foreground text-right">
            <div>生成日時</div>
            <div>{editedResult.metadata?.generatedAt ? new Date(editedResult.metadata.generatedAt).toLocaleString('ja-JP') : '不明'}</div>
          </div>
        </div>
      </div>
      
      {/* メインテキストビューア */}
      <SceneTextViewer 
        text={editedResult.fullText || ''}
        paragraphs={editedResult.paragraphs || []}
      />

      {/* メタ情報（折りたたみ） */}
      {editedResult.metadata && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground">生成パラメータ</summary>
          <div className="mt-2 p-3 bg-muted rounded-md space-y-1">
            <div>モード: {editedResult.metadata.mode || 'standard'}</div>
            {editedResult.metadata.parameters?.genre && (
              <div>ジャンル: {editedResult.metadata.parameters.genre}</div>
            )}
            {editedResult.metadata.parameters?.perspective && (
              <div>視点: {
                editedResult.metadata.parameters.perspective === 'first' ? '一人称' :
                editedResult.metadata.parameters.perspective === 'third_limited' ? '三人称限定' :
                editedResult.metadata.parameters.perspective === 'third_omniscient' ? '三人称神視点' :
                '二人称'
              }</div>
            )}
            {editedResult.metadata.parameters?.writingStyle && (
              <div>文体: {editedResult.metadata.parameters.writingStyle}</div>
            )}
            {editedResult.metadata.parameters?.targetLength && (
              <div>目標文字数: {editedResult.metadata.parameters.targetLength}</div>
            )}
            {editedResult.metadata.parameters?.actualLength && (
              <div>実際の文字数: {editedResult.metadata.parameters.actualLength}</div>
            )}
            {editedResult.metadata.parameters?.creativityLevel && (
              <div>創造性レベル: {editedResult.metadata.parameters.creativityLevel}/10</div>
            )}
            {editedResult.metadata.parameters?.useProMode && (
              <div>Proモード使用</div>
            )}
            {editedResult.metadata.continuationInfo && (
              <>
                <div className="pt-2 border-t">連続生成情報:</div>
                <div>前のシーンID: {editedResult.metadata.continuationInfo.previousSceneId}</div>
                <div>継続タイプ: {editedResult.metadata.continuationInfo.continuationType}</div>
              </>
            )}
          </div>
        </details>
      )}
      
      {/* 編集モーダル */}
      {isEditing && (
        <SceneEditModal
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          onSave={(updatedResult) => {
            const newResult = { ...editedResult, ...updatedResult }
            setEditedResult(newResult)
            onSave(newResult)
            setIsEditing(false)
          }}
          sceneResult={editedResult}
          title="シーンを編集"
        />
      )}
    </div>
  )
}